import { useState } from "react";
import { Copy, Mail, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../ui/switch";
import { useApi } from "../../hooks/useApi";
import { web, errMsg } from "../../lib/api";
import { Panel, Loader, ErrorNote, Btn, shortDate } from "./kit";
import { Field, TextInput, Chips } from "./Form";
import { useInviteWebsite } from "../../hooks/useInviteWebsite";

const KINDS = [["recruiter", "Recruiter"], ["coach", "Coach (all their teams)"]];
export const TAGS = [["partner", "Partner"], ["pilot_school", "Pilot school"], ["staff", "Staff"], ["other", "Other"]];
export const tagLabel = (t) => TAGS.find(([k]) => k === t)?.[1];
const countIn = (rows) => (k) => (k === "all" ? rows.length : rows.filter((g) => (k === "none" ? !g.tag : g.tag === k)).length);

export const TagFilter = ({ count, value, onChange, prefix = "comp-filter" }) => {
  const opts = [["all", "All"], ...TAGS, ["none", "No reason"]];
  return (
    <div className="mb-4 flex flex-wrap gap-2" role="tablist">
      {opts.map(([k, l]) => (
        <button key={k} role="tab" aria-selected={value === k} data-testid={`${prefix}-${k}`} onClick={() => onChange(k)}
          className={`rounded-full border px-3 py-1.5 font-mono-gd text-xs uppercase tracking-wider transition-colors ${value === k ? "border-[#39FF14] bg-[#39FF14] text-[#0A0E1A]" : "border-[#1E293B] text-slate-300 hover:text-white"}`}>
          {l} <span className="opacity-70">{count(k)}</span>
        </button>
      ))}
    </div>
  );
};

const GrantForm = ({ onSaved }) => {
  const [f, setF] = useState({ email: "", name: "", kind: "recruiter", tag: "", send_invite: true });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) return toast.error("Enter a valid email");
    setBusy(true);
    try {
      const { data } = await web.post("/admin/comp-accounts", { ...f, email: f.email.trim(), name: f.name.trim() || null, tag: f.tag || null });
      toast.success(`Free access on for ${data.email}${f.send_invite ? ". Invite sent" : ""}`);
      setF((x) => ({ ...x, email: "", name: "" }));
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); onSaved(); }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="comp-account-form" className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Email"><TextInput data-testid="comp-account-email" type="email" value={f.email} onChange={(e) => setF((x) => ({ ...x, email: e.target.value }))} placeholder="coach@school.edu" /></Field>
        <Field label="Name (optional)"><TextInput data-testid="comp-account-name" value={f.name} onChange={(e) => setF((x) => ({ ...x, name: e.target.value }))} placeholder="First and last name" /></Field>
      </div>
      <Field label="Account type"><Chips testid="comp-account-kind" options={KINDS} value={f.kind} onChange={(kind) => setF((x) => ({ ...x, kind }))} /></Field>
      <Field label="Reason (optional)"><Chips testid="comp-account-tag" options={TAGS} value={f.tag} onChange={(tag) => setF((x) => ({ ...x, tag: x.tag === tag ? "" : tag }))} /></Field>
      <label className="flex items-center gap-3 text-sm text-slate-200">
        <Switch data-testid="comp-account-invite-switch" checked={f.send_invite} onCheckedChange={(v) => setF((x) => ({ ...x, send_invite: v }))} className="data-[state=checked]:bg-[#39FF14]" />
        Email them an invite link to sign up or log in
      </label>
      <Btn type="submit" disabled={busy} data-testid="comp-account-grant"><UserPlus className="h-4 w-4" />{busy ? "Saving…" : "Grant free access"}</Btn>
    </form>
  );
};

const GrantRow = ({ g, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const site = useInviteWebsite();
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copy = () => act(async () => { const { data } = await web.get(`/admin/comp-accounts/${g.id}/link`); await navigator.clipboard.writeText(`${data.link}\nLearn about GAMEDAY Courtside: ${site}`); }, "Invite link copied");
  return (
    <li data-testid={`comp-account-${g.id}`} className="flex flex-col gap-3 rounded-2xl border border-[#1E293B] bg-[#0F172A] px-5 py-4 lg:flex-row lg:items-center">
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-2 truncate font-semibold text-white">{g.name || g.email}{g.tag && <span data-testid={`comp-account-tag-badge-${g.id}`} className="rounded-full bg-[#39FF14]/15 px-2 py-0.5 font-mono-gd text-[10px] uppercase tracking-wider text-[#39FF14]">{tagLabel(g.tag)}</span>}</p>
        <p className="truncate text-sm text-slate-400">{g.name ? `${g.email} · ` : ""}{g.kind === "coach" ? "Coach" : "Recruiter"} · since {shortDate(g.granted_at)}{g.invite_sent_at ? ` · invited ${shortDate(g.invite_sent_at)}` : ""}</p>
        {g.kind === "coach" && <p data-testid={`comp-account-teams-${g.id}`} className="mt-1 truncate font-mono-gd text-[11px] uppercase tracking-wider text-slate-400">{g.teams?.length ? `Teams: ${g.teams.map((t) => t.name).join(", ")}` : "No teams yet. Their teams unlock when they first open the website"}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid={`comp-account-tag-select-${g.id}`} aria-label="Reason" value={g.tag ?? ""} disabled={busy}
          onChange={(e) => act(() => web.post(`/admin/comp-accounts/${g.id}/tag`, { tag: e.target.value || null }), "Reason saved")}
          className="rounded-full border border-[#1E293B] bg-[#0A0E1A] px-3 py-1.5 text-xs font-semibold text-slate-200 outline-none focus:border-[#39FF14]">
          <option value="">No reason</option>
          {TAGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button data-testid={`comp-account-resend-${g.id}`} disabled={busy} onClick={() => act(() => web.post(`/admin/comp-accounts/${g.id}/invite`), "Invite sent")} className="inline-flex items-center gap-1 rounded-full border border-[#1E293B] px-3 py-1.5 text-xs font-semibold text-slate-200 hover:border-[#39FF14]/60 disabled:opacity-50"><Mail className="h-3.5 w-3.5" />{g.invite_sent_at ? "Resend invite" : "Send invite"}</button>
        <button data-testid={`comp-account-copy-${g.id}`} disabled={busy} onClick={copy} className="inline-flex items-center gap-1 rounded-full border border-[#1E293B] px-3 py-1.5 text-xs font-semibold text-slate-200 hover:border-[#39FF14]/60 disabled:opacity-50"><Copy className="h-3.5 w-3.5" />Copy link</button>
        <label className="ml-1 flex items-center gap-2">
          <span className={`font-mono-gd text-xs uppercase tracking-wider ${g.enabled ? "text-[#39FF14]" : "text-slate-400"}`}>{g.enabled ? "Free access" : "Off"}</span>
          <Switch data-testid={`comp-account-switch-${g.id}`} checked={!!g.enabled} disabled={busy} onCheckedChange={(v) => act(() => web.post(`/admin/comp-accounts/${g.id}/toggle`, { enabled: v }), v ? "Free access on" : "Free access off")} className="data-[state=checked]:bg-[#39FF14]" />
        </label>
      </div>
    </li>
  );
};

/** Admin: free access for individual recruiter and coach accounts, by email (works before they sign up). */
export const AccountGrants = ({ onChanged }) => {
  const list = useApi("/admin/comp-accounts", web);
  const reload = () => { list.reload(); onChanged?.(); };
  const rows = Array.isArray(list.data) ? list.data : [];
  const [tag, setTag] = useState("all");
  const shown = rows.filter((g) => tag === "all" || (tag === "none" ? !g.tag : g.tag === tag));
  return (
    <Panel data-testid="comp-accounts" className="mb-10">
      <h2 className="font-display text-2xl font-bold uppercase text-white">Free access for people</h2>
      <p className="mb-5 mt-1 max-w-2xl text-sm text-slate-300">Give a recruiter or coach free access by email, even before they sign up. Coaches get free access on every team they own.</p>
      <GrantForm onSaved={reload} />
      <div className="mt-6">
        {list.loading ? <Loader /> : list.error ? <ErrorNote error={list.error} /> : rows.length ? (<>
          <TagFilter count={countIn(rows)} value={tag} onChange={setTag} />
          {shown.length ? <ul className="space-y-3">{shown.map((g) => <GrantRow key={g.id} g={g} onChanged={reload} />)}</ul>
            : <p className="text-sm text-slate-400" data-testid="comp-accounts-filter-empty">No one with this reason.</p>}
        </>) : <p className="text-sm text-slate-400" data-testid="comp-accounts-empty">No one has individual free access yet.</p>}
      </div>
    </Panel>
  );
};
